import { useAutosave, useNote, useServices } from "@hq/client-react";
import { deriveTitle, newId, noteLink } from "@hq/core";
import {
  MarkdownEditor,
  type MarkdownEditorHandle,
  type MarkdownSelection,
} from "@hq/markdown-editor";
import { cn, EmptyState, Skeleton, toast } from "@z/ui";
import { type ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ExtractNoteDialog } from "./ExtractNoteDialog";
import { AddTodoPopover } from "./AddTodo";
import { lineToTodoTitle, type AddTodoRequest } from "./commands";
import type { NoteEditorHandle, NoteEditorProps } from "./NoteEditor";

export function MarkdownNoteEditor({
  noteId,
  autoFocus,
  onTitleChange,
  onOpenNote,
  className,
  beforeContent,
  toolbarStart,
  toolbarEnd,
  ref,
}: NoteEditorProps & {
  beforeContent?: ReactNode;
  toolbarStart?: ReactNode;
  toolbarEnd?: ReactNode;
}) {
  const { data: note, isLoading } = useNote(noteId);
  const autosave = useAutosave();
  const services = useServices();
  const editor = useRef<MarkdownEditorHandle>(null);
  const [extract, setExtract] = useState<MarkdownSelection | null>(null);
  const [moving, setMoving] = useState(false);
  const moveSelection = async (title: string) => {
    if (!extract || moving) return;
    setMoving(true);
    try {
      await editor.current?.flush();
      await autosave.flush(noteId);
      const child = { id: newId(), title: title.trim() || deriveTitle(extract.markdown) };
      // The blocks go, and a link to where they went takes their place: one undo step, saved with
      // the new note in one transaction.
      const source = await editor.current?.replaceBlocks(extract.blockIds, noteLink(child));
      if (!source) throw new Error("The note changed. Select the text again and retry.");
      try {
        await services.notes.extract(noteId, {
          child: { ...child, markdown: extract.markdown },
          source,
        });
      } catch (error) {
        editor.current?.undo();
        throw error;
      }
      setExtract(null);
      toast({
        title: "Moved to a new note",
        ...(onOpenNote
          ? { action: { label: "Open note", onClick: () => onOpenNote(child.id) } }
          : {}),
      });
    } catch (error) {
      toast({
        title: "Could not move selection",
        description: error instanceof Error ? error.message : String(error),
        variant: "danger",
      });
    } finally {
      setMoving(false);
    }
  };
  const pending = useRef("");
  const [request, setRequest] = useState<AddTodoRequest | null>(null);
  const addTodo = (selection: MarkdownSelection) =>
    setRequest({
      selectionText: selection.text,
      lineText: selection.line,
      title: lineToTodoTitle(selection.text || selection.line),
      coords: selection.coords,
    });
  useImperativeHandle(ref, (): NoteEditorHandle => ({
    focus: () => editor.current?.focus(),
    getDoc: () => editor.current?.getDoc() ?? "",
    insertText: (text) => {
      if (editor.current) editor.current.insertText(text);
      else pending.current += text;
    },
    getLine: () => editor.current?.getSelection().line ?? "",
    setGhostText: (text) => editor.current?.setGhostText(text),
    openAddTodo: () => {
      const selection = editor.current?.getSelection();
      if (selection) addTodo(selection);
    },
  }));
  useEffect(() => {
    const flush = () => {
      void autosave.flush(noteId);
    };
    const visibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("pagehide", flush);
    return () => {
      flush();
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("pagehide", flush);
    };
  }, [noteId, autosave]);
  if (isLoading) return <Skeleton className="mx-6 my-8 h-24" />;
  if (!note) return <EmptyState title="Note not found" />;
  return (
    <div data-note-editor={noteId} className={cn("h-full min-h-0 overflow-y-auto", className)}>
      <div inert={moving}>
        <MarkdownEditor
          key={noteId}
          ref={(handle) => {
            editor.current = handle;
            if (handle && pending.current) {
              handle.insertText(pending.current);
              pending.current = "";
            }
          }}
          documentId={noteId}
          value={{ document: note.document, markdown: note.markdown }}
          revision={note.revision}
          autoFocus={autoFocus}
          className="min-h-48 pb-10"
          beforeContent={beforeContent}
          toolbarStart={toolbarStart}
          toolbarEnd={toolbarEnd}
          contentClassName="mx-auto min-h-[280px] w-full max-w-[800px] px-6 @md/editor:px-12"
          acceptExternalChange={() => ["idle", "saved"].includes(autosave.getState(noteId))}
          onChange={(content, baseRevision) => {
            autosave.update(noteId, content, baseRevision);
            onTitleChange?.(deriveTitle(content.markdown));
          }}
          onBlur={() => void autosave.flush(noteId)}
          onAddTodo={addTodo}
          onNavigate={
            onOpenNote
              ? (url) => {
                  const match = /^\/notes\/([a-f0-9-]+)$/i.exec(url);
                  if (!match) return false;
                  onOpenNote(match[1]!);
                  return true;
                }
              : undefined
          }
          onExtract={(selection) => {
            if (selection.text.trim() && selection.blockIds.length > 0) setExtract(selection);
          }}
        />
      </div>
      {extract && (
        <ExtractNoteDialog
          text={extract.text}
          busy={moving}
          onMove={(title) => void moveSelection(title)}
          onClose={() => {
            setExtract(null);
            editor.current?.focus();
          }}
        />
      )}
      {request && (
        <AddTodoPopover
          sourceNoteId={noteId}
          request={request}
          onClose={() => {
            setRequest(null);
            editor.current?.focus();
          }}
        />
      )}
    </div>
  );
}
